import React from 'react'
import styled from 'styled-components'
import { Box, TextBody } from '@ally/metronome-ui'
import { DepositAccount, InvestAccount } from '@ally/data-types-accounts'
import { maskAccountNumber } from '@ally/aos-utils'
import { AccountType } from '@ally/data-types-afg-accounts'
import {
  AccountTypeToLabel,
  getBalance,
  labelHeadings,
  getDashDash,
  LabelHeadingKeys,
} from '../../utils'
import { STALE_DATA_COPY } from '../../constants'
import { TextBodyOverride } from '../MetronomeOverrides'

const StyledBoxWrapper = styled(Box)`
  white-space: normal;
  cursor: pointer;
  /* make sure every child has the cursor pointer  */
  * {
    cursor: pointer;
  }
`

interface LocaleOptions {
  month: '2-digit' | 'numeric' | 'short' | 'long' | 'narrow' | undefined
  day: '2-digit' | 'numeric' | undefined
  year: '2-digit' | 'numeric' | undefined
}

const ClosedAccount: React.FC<{ accountClosingDate: string }> = ({
  accountClosingDate,
}) => {
  const options: LocaleOptions = {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }
  return (
    <TextBody tag="p" size="sm" type="error">
      {new Date(accountClosingDate).toLocaleDateString('en-US', options)}
    </TextBody>
  )
}

interface GetAccountValueProps {
  accountClosingDate?: string
  FormattedCurrency: React.ReactElement
  dashDashAssistiveText?: string
}

export const getAccountValue = ({
  accountClosingDate,
  FormattedCurrency,
  dashDashAssistiveText,
}: GetAccountValueProps): React.ReactNode => {
  if (accountClosingDate) {
    return <ClosedAccount accountClosingDate={accountClosingDate} />
  }
  if (dashDashAssistiveText) {
    return getDashDash(dashDashAssistiveText)
  }

  return FormattedCurrency
}

const FormattedCurrency: React.FC<{ balance: number; pastDue: boolean }> = ({
  balance,
  pastDue,
}) => (
  <TextBodyOverride
    tag="p"
    size="md"
    color={pastDue || balance < 0 ? 'error' : 'black'}
  >
    {Intl.NumberFormat('en-us', {
      style: 'currency',
      currency: 'USD',
    }).format(balance)}
  </TextBodyOverride>
)

type GetMobileCardValueHeadingProps = {
  accType: string
  accStatus: string
}

export const getMobileCardValueHeading = ({
  accType,
  accStatus,
}: GetMobileCardValueHeadingProps): React.ReactNode => {
  if (accStatus === 'Closed') {
    return (
      <TextBody tag="p" size="md" type="error">
        CLOSED
      </TextBody>
    )
  }

  const accountTypeLabel = AccountTypeToLabel[
    accType as AccountType
  ] as LabelHeadingKeys
  const valueHeading = labelHeadings[accountTypeLabel]

  if (valueHeading) {
    return (
      <TextBody tag="p" size="md" weight="bold">
        {valueHeading}
      </TextBody>
    )
  }

  return null
}

const LegacyMobileCardDetail: React.FC<{
  account: DepositAccount | InvestAccount
  hasStaleData?: boolean
}> = ({ account, hasStaleData }) => {
  const {
    accountNickname,
    accountNumberPvtEncrypt,
    accountType,
    accountStatus,
  } = account

  // istanbul ignore next
  const accNickname = accountNickname
  const accNumberPvtEncrypt = accountNumberPvtEncrypt
  const accType = accountType
  const accStatus = accountStatus
  const isDepositAccount =
    (account as DepositAccount).availableBalancePvtEncrypt !== undefined
  const isInvestAccount =
    (account as InvestAccount).accountValueTotal !== undefined

  const handleAvailableBalance = (): number => {
    if (isDepositAccount) {
      return (account as DepositAccount).availableBalancePvtEncrypt
    }
    return (account as InvestAccount).accountValueTotal
  }
  const handleCurrentBalance = (): number => {
    if (isDepositAccount) {
      return (account as DepositAccount).currentBalancePvtEncrypt
    }

    return (account as InvestAccount).accountValueTotal
  }

  const accountClosingDate =
    (account as DepositAccount).accountClosingDate || ''
  const availableBalance = handleAvailableBalance()
  const currentBalance = handleCurrentBalance()
  const showBalance =
    isDepositAccount || (isInvestAccount && accStatus !== 'Closed')

  const getAssistiveText = (): string | undefined => {
    if (hasStaleData) {
      return STALE_DATA_COPY
    }

    return undefined
  }

  const nicknameSpan = (
    <TextBodyOverride tag="span" color="bluesuedeshoes" size="lg" weight="bold">
      {accNickname}
    </TextBodyOverride>
  )

  return (
    <StyledBoxWrapper>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        borderLeftStyle="solid"
        borderLeftWidth="7px"
        p="xs"
        borderTop="1px solid"
        borderTopColor="slate-2"
        width="100%"
      >
        <Box
          display="flex"
          flex={1}
          flexDirection="column"
          alignItems="flex-start"
          textAlign="left"
        >
          <Box display="flex" flexDirection="column">
            <div>
              <Box display="flex" alignItems="center">
                {nicknameSpan}
              </Box>
            </div>
            <TextBody tag="span" size="sm">
              {maskAccountNumber(accNumberPvtEncrypt)}
            </TextBody>
          </Box>
        </Box>
        <Box
          display="flex"
          flex={1}
          flexDirection="column"
          alignItems="flex-end"
        >
          <Box>
            <TextBody tag="p" size="xs" weight="bold">
              {getMobileCardValueHeading({
                accType,
                accStatus,
              })}
            </TextBody>
          </Box>
          {showBalance && (
            <Box>
              {getAccountValue({
                accountClosingDate,
                FormattedCurrency: (
                  <FormattedCurrency
                    balance={getBalance({
                      accountType: accType,
                      availableBalance,
                      currentBalance,
                      paymentDue: undefined,
                    })}
                    pastDue={false}
                  />
                ),
                dashDashAssistiveText: getAssistiveText(),
              })}
            </Box>
          )}
        </Box>
      </Box>
    </StyledBoxWrapper>
  )
}

export default LegacyMobileCardDetail
